import { useMemo } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { orderBy, query, Timestamp, where } from "firebase/firestore";
import type {
  GenerationMetadataEvent,
  OnixImportMetadataEvent,
} from "reedy-data/models";
import { getMetadataEventsCollection } from "~/integrations/firebase/collections";

export type OnixImportEvent = OnixImportMetadataEvent;
export type GenerationEvent = GenerationMetadataEvent;

export interface ReleaseDateEvent {
  id: "release-date";
  type: "RELEASE_DATE";
  occurredAt: Date;
}

export type MetadataEvent =
  | OnixImportEvent
  | GenerationEvent
  | ReleaseDateEvent;

export const useMetadataEvents = ({
  productId,
  organisationId,
  from,
  to,
  publishedAt,
  enabled = true,
}: {
  productId?: string;
  organisationId?: string;
  from?: Date;
  to?: Date;
  publishedAt?: Date;
  enabled?: boolean;
}): { events: MetadataEvent[]; isLoading: boolean; isError: boolean } => {
  const fromMs = from?.getTime();
  const toMs = to?.getTime();

  const eventsQuery = useMemo(
    () =>
      enabled && productId && organisationId && fromMs != null && toMs != null
        ? query(
            getMetadataEventsCollection(),
            where("organisation.id", "==", organisationId),
            where("productId", "==", productId),
            where("occurredAt", ">=", Timestamp.fromMillis(fromMs)),
            where("occurredAt", "<=", Timestamp.fromMillis(toMs)),
            orderBy("occurredAt"),
          )
        : undefined,
    [enabled, productId, organisationId, fromMs, toMs],
  );

  const [values, isLoading, error] = useCollectionData(eventsQuery);

  const isActive = eventsQuery != null;

  const events = useMemo<MetadataEvent[]>(() => {
    const collected: MetadataEvent[] = [...(values ?? [])];
    if (
      enabled &&
      publishedAt &&
      fromMs != null &&
      toMs != null &&
      publishedAt.getTime() >= fromMs &&
      publishedAt.getTime() <= toMs
    ) {
      collected.push({
        id: "release-date",
        type: "RELEASE_DATE",
        occurredAt: publishedAt,
      });
    }
    return collected.sort(
      (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
    );
  }, [enabled, values, publishedAt, fromMs, toMs]);

  return { events, isLoading: isActive && isLoading, isError: Boolean(error) };
};
